// Per-game ephemeral SSE client.
//
// Carries short-lived, non-persisted "gestures" between players in a game
// (placement hover, drag preview, pointer signals, etc.). Backend route is
// /api/games/:id/ephemeral-stream for receive and POST /api/games/:id/
// ephemeral for publish.
//
// Usage:
//   const eph = new EphemeralClient(gameId);
//   await eph.connect();
//   const off = eph.subscribe('placement-hover', (msg) => {...});
//   eph.publish('placement-hover', { col, row, characterId });
//   // on teardown:
//   off(); eph.disconnect();

import { api, getBackendUrl } from './api.js';
import { authState } from './auth.js';

export class EphemeralClient {
  /** @param {string} gameId */
  constructor(gameId) {
    this.gameId = gameId;
    /** @type {EventSource|null} */
    this._source = null;
    /** @type {Map<string, Set<(msg: any) => void>>} */
    this._handlers = new Map();
  }

  async connect() {
    if (this._source) return;
    const token = await authState.getAccessToken();
    if (!token) return;
    const url = `${getBackendUrl()}/api/games/${encodeURIComponent(this.gameId)}/ephemeral-stream?token=${encodeURIComponent(token)}`;
    const source = new EventSource(url);
    source.onmessage = (e) => this._dispatch(e.data);
    source.onerror = (err) => {
      console.warn('[ephemeral] stream error, browser will reconnect', err);
    };
    this._source = source;
  }

  disconnect() {
    if (this._source) {
      this._source.close();
      this._source = null;
    }
    this._handlers.clear();
  }

  /**
   * @param {string} type
   * @param {(msg: { type: string, playerId: string, data?: any }) => void} handler
   * @returns {() => void}
   */
  subscribe(type, handler) {
    if (!this._handlers.has(type)) this._handlers.set(type, new Set());
    const set = /** @type {Set<(msg: any) => void>} */ (this._handlers.get(type));
    set.add(handler);
    return () => {
      set.delete(handler);
      if (set.size === 0) this._handlers.delete(type);
    };
  }

  /**
   * @param {string} type
   * @param {any} [data]
   */
  publish(type, data) {
    api
      .post(`/api/games/${encodeURIComponent(this.gameId)}/ephemeral`, { type, data })
      .catch((err) => console.warn('[ephemeral] publish failed', err));
  }

  /** @param {string} raw */
  _dispatch(raw) {
    let msg;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (!msg || typeof msg.type !== 'string') return;

    // ephemeral:clear is a synthetic frame from the broadcaster when a
    // player disconnects — relay to handlers that care, but also clear
    // any per-type "last message from this player" caches the caller may
    // be holding via a special handler key '*'.
    if (msg.type === 'ephemeral:clear') {
      const wildcard = this._handlers.get('*');
      if (wildcard) for (const h of wildcard) h(msg);
      return;
    }

    const set = this._handlers.get(msg.type);
    if (!set) return;
    for (const h of set) h(msg);
  }
}
